import type { PrescriptionItemRequest } from '@/api/types'
import { CellCombobox } from '@/components/CellCombobox'
import { DURATIONS, FREQUENCIES, INSTRUCTIONS } from '@/lib/clinicalOptions'
import { RowEditor, type RowField } from './RowEditor'

export const emptyItem = (): PrescriptionItemRequest => ({
  drug_name: '',
  dose: null,
  frequency: null,
  duration: null,
  instructions: null,
})

/**
 * Frequency is its own column rather than folded into dose: the record stores
 * the two separately and every read-only view renders both.
 *
 * Only the drug name is required — "half a tablet at night" is a real
 * prescription, and a validation error there stops a consultation being saved.
 *
 * Widths are measured against the uppercase headers; an equal share ran
 * "Frequency" into "Duration".
 */
const FIELDS: RowField<PrescriptionItemRequest>[] = [
  {
    key: 'drug_name',
    header: 'Medicine',
    width: 'w-[27%]',
    label: (i) => `Medicine ${i + 1}`,
    render: ({ row, className, label, onKeyDown, set }) => (
      <input
        className={className}
        aria-label={label}
        placeholder="Paracetamol 500"
        value={row.drug_name}
        onKeyDown={onKeyDown}
        onChange={(e) => set({ drug_name: e.target.value })}
      />
    ),
  },
  {
    key: 'dose',
    header: 'Dose',
    width: 'w-[13%]',
    half: true,
    label: (i) => `Dose for medicine ${i + 1}`,
    render: ({ row, className, label, onKeyDown, set }) => (
      <input
        className={className}
        aria-label={label}
        // How much, not when. The old "1-0-1" here invited the frequency into
        // the dose column.
        placeholder="1 tablet"
        value={row.dose ?? ''}
        onKeyDown={onKeyDown}
        onChange={(e) => set({ dose: e.target.value || null })}
      />
    ),
  },
  {
    key: 'frequency',
    header: 'Frequency',
    width: 'w-[20%]',
    half: true,
    label: (i) => `Frequency for medicine ${i + 1}`,
    render: ({ row, className, label, onKeyDown, set }) => (
      <CellCombobox
        className={className}
        label={label}
        placeholder="1-0-1"
        options={FREQUENCIES}
        value={row.frequency ?? ''}
        onKeyDown={onKeyDown}
        onChange={(next) => set({ frequency: next || null })}
      />
    ),
  },
  {
    key: 'duration',
    header: 'Duration',
    width: 'w-[18%]',
    half: true,
    label: (i) => `Duration for medicine ${i + 1}`,
    render: ({ row, className, label, onKeyDown, set }) => (
      <CellCombobox
        className={className}
        label={label}
        placeholder="3 days"
        options={DURATIONS}
        value={row.duration ?? ''}
        onKeyDown={onKeyDown}
        onChange={(next) => set({ duration: next || null })}
      />
    ),
  },
  {
    key: 'instructions',
    header: 'Remarks',
    width: 'w-[22%]',
    half: true,
    label: (i) => `Remarks for medicine ${i + 1}`,
    render: ({ row, className, label, onKeyDown, set }) => (
      <CellCombobox
        className={className}
        label={label}
        placeholder="After food"
        options={INSTRUCTIONS}
        value={row.instructions ?? ''}
        onKeyDown={onKeyDown}
        onChange={(next) => set({ instructions: next || null })}
      />
    ),
  },
]

export function PrescriptionTable({
  items,
  onChange,
}: {
  items: PrescriptionItemRequest[]
  onChange: (items: PrescriptionItemRequest[]) => void
}) {
  return (
    <RowEditor
      noun="Medicine"
      regionLabel="Prescription"
      fields={FIELDS}
      focusField="drug_name"
      items={items}
      onChange={onChange}
      blank={emptyItem}
      empty="No medicines added yet."
    />
  )
}
